import { useCallback, useRef } from "react";
import type { InstantSearchProps } from "react-instantsearch";
import {
  flushSearchSession,
  getSearchSession,
  rememberSearchUiState,
  type SearchSessionKey,
} from "@/lib/search-session-store";

type UiState = NonNullable<InstantSearchProps["initialUiState"]>;
type OnStateChange = NonNullable<InstantSearchProps["onStateChange"]>;

interface UseSearchSessionUiStateOptions {
  /** uiState derived from URL params; when present it overrides the restored session. */
  urlUiState?: UiState;
  /** True when URL params override the session without mapping to uiState (e.g. where-params). */
  hasUrlOverride?: boolean;
  /** Per-index uiState keys whose widgets are conditionally mounted — never
   *  persisted, or their restored entries would be silently dropped and
   *  diverge from the cached hits. Pass a module-level const. */
  omit?: string[];
}

interface UseSearchSessionUiStateReturn {
  initialUiState: UiState | undefined;
  onStateChange: OnStateChange;
}

/**
 * Restores and persists a search page's InstantSearch uiState through the
 * module-scoped session store, under one precedence rule: a URL carrying any
 * of the page's owned search params is the single source of truth for that
 * mount; the session store is the fallback for URL-less entry only. An
 * override flushes the session's cached hits/snapshot once per mount (they
 * belong to a different search) and, when it maps to uiState, replaces the
 * stored state so the next override-less visit restores what was actually
 * searched. Callers must pass `urlUiState: undefined` — never `{}` — when the
 * URL is bare, or the empty override would beat the restored session. The
 * page's UrlSync writer fills the URL from live state right after mount, so
 * the two sources converge within a frame.
 */
export function useSearchSessionUiState(
  key: SearchSessionKey,
  { urlUiState, hasUrlOverride = urlUiState !== undefined, omit }: UseSearchSessionUiStateOptions = {},
): UseSearchSessionUiStateReturn {
  const session = getSearchSession(key);
  const urlOverrideHandled = useRef(false);

  if (hasUrlOverride && !urlOverrideHandled.current) {
    urlOverrideHandled.current = true;
    flushSearchSession(key);
    if (urlUiState) session.uiState = urlUiState;
  }

  const onStateChange = useCallback<OnStateChange>(
    ({ uiState, setUiState }) => {
      rememberSearchUiState(key, uiState, omit);
      setUiState(uiState);
    },
    [key, omit],
  );

  return { initialUiState: urlUiState ?? session.uiState, onStateChange };
}
